/**
 * Périmètre société/secteur d'une application (après filtre application_ids).
 * Listes vides = aucune restriction de lieu.
 *
 * Invariant admin : pour une société, soit company_ids (tous secteurs),
 * soit sector_ids partiels — pas les deux en même temps.
 */
export function isAppOutOfSecteurScope(app, scopeCtx) {
  if (!scopeCtx) return false;
  const companyIds = Array.isArray(app?.company_ids) ? app.company_ids.map(String) : [];
  const sectorIds = Array.isArray(app?.sector_ids) ? app.sector_ids.map(String) : [];
  if (companyIds.length === 0 && sectorIds.length === 0) return false;

  const actifSecteurId = scopeCtx.actifSecteurId != null ? String(scopeCtx.actifSecteurId) : '';
  const actifCompanyId = scopeCtx.actifCompanyId != null ? String(scopeCtx.actifCompanyId) : '';

  if (actifSecteurId && sectorIds.includes(actifSecteurId)) return false;
  if (actifCompanyId && companyIds.includes(actifCompanyId)) return false;
  return true;
}

/** Application désactivée côté admin (is_active explicite à false) ou hors périmètre secteur. */
export function isAppDisabled(app, scopeCtx) {
  return app?.is_active === false || isAppOutOfSecteurScope(app, scopeCtx);
}

/** Ouverture bloquée : hors périmètre toujours ; inactive sauf admin. */
export function isAppOpenBlocked(app, user, scopeCtx) {
  if (isAppOutOfSecteurScope(app, scopeCtx)) return true;
  return app?.is_active === false && user?.is_admin !== true;
}

export function getDisabledAppBodyClickTitle(app, user, scopeCtx) {
  if (isAppOutOfSecteurScope(app, scopeCtx)) {
    return 'Non disponible sur ce secteur — changez de secteur pour y accéder';
  }
  if (!isAppDisabled(app, scopeCtx)) return 'Cliquez pour voir les détails';
  if (user?.is_admin) return 'Application désactivée pour les utilisateurs — cliquez pour ouvrir';
  return 'Cliquez pour voir le motif et la disponibilité estimée';
}

export function getAppOpenButtonClassName(disabledLook, { fullWidth = true, compact = false } = {}) {
  const size = compact
    ? 'border-2 text-xs px-3 py-1 h-auto cursor-pointer'
    : 'w-full border-2 font-medium rounded-lg py-1.5 text-xs cursor-pointer';
  const width = fullWidth && !compact ? 'w-full ' : '';
  if (disabledLook) {
    return `${width}${size} border-gray-300 bg-transparent text-gray-400 hover:bg-gray-200/40 dark:hover:bg-gray-700/40 dark:border-gray-600 dark:text-gray-500`;
  }
  return `${width}${size} border-[#f76f4b] bg-transparent hover:bg-[#f76f4b]/10 text-[#f76f4b]`;
}

/** Carte / vignette « Mes applications » */
export function getDisabledAppCardClass(disabled, activeClasses = '') {
  if (!disabled) return activeClasses;
  return [
    activeClasses,
    'bg-gray-100/95 dark:bg-gray-800/75',
    'opacity-60 saturate-[0.4]',
    'shadow-sm',
  ]
    .filter(Boolean)
    .join(' ');
}

/** Lien texte dans un dossier de groupe */
export function getDisabledAppLinkClass(disabled) {
  if (!disabled) {
    return 'text-xs text-gray-700 dark:text-gray-300 cursor-pointer hover:text-[#f76f4b] dark:hover:text-[#f76f4b] transition-colors truncate';
  }
  return 'text-xs text-gray-400 dark:text-gray-500 cursor-pointer truncate';
}

/** Pastille dans la vue liste des groupes */
export function getDisabledAppChipClass(disabled) {
  if (!disabled) {
    return 'text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded cursor-pointer hover:bg-[#f76f4b]/20';
  }
  return 'text-xs bg-gray-200/90 dark:bg-gray-700/90 text-gray-400 dark:text-gray-500 px-2 py-1 rounded cursor-pointer';
}

/** Ligne favori sidebar */
export function getDisabledFavoriteRowClass(disabled, base = '') {
  if (!disabled) return base;
  return `${base} opacity-55 saturate-50`.trim();
}

/** Icône initiale favori (sidebar réduite) */
export function getDisabledFavoriteIconClass(disabled) {
  if (!disabled) {
    return 'w-8 h-8 rounded-lg bg-[#f76f4b] bg-opacity-10 flex items-center justify-center flex-shrink-0 cursor-pointer';
  }
  return 'w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 cursor-pointer opacity-70';
}

export function getDisabledFavoriteLetterClass(disabled) {
  if (!disabled) return 'text-sm font-bold text-[#f76f4b]';
  return 'text-sm font-bold text-gray-400 dark:text-gray-500';
}
